import React, { useEffect, useState } from "react";
import smsgatewayService from "../../../../../services/smsgateway.service";
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Modal, Button } from 'react-bootstrap';

function SmsgatewayTab() {
    const [loading, setLoading] = useState(true);
    const [tokens, setTokens] = useState([]);
    const { clinicId } = useParams();
    const [inputValues, setInputValues] = useState({});
    const [creatingToken, setCreatingToken] = useState(false);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [selectedToken, setSelectedToken] = useState(null);

    useEffect(() => {
        getTokenList();
    }, [clinicId]);

    const getTokenList = () => {
        setLoading(true);
        smsgatewayService.getTokenList(clinicId)
            .then(tokens => {
                const tokensWithState = tokens.data.map(token => ({
                    ...token,
                    showConfirmation: false
                }));
                setTokens(tokensWithState);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error loading tokens',
                    timer: 3000,
                    showConfirmButton: false
                });
            });
    }

    const revealToken = (token) => {
        setSelectedToken(token);
        setShowTokenModal(true);
    }

    const copyTokenToClipboard = () => {
        if (selectedToken) {
            navigator.clipboard.writeText(selectedToken.token)
                .then(() => {
                    Swal.fire({
                        icon: 'success',
                        title: 'Copied!',
                        text: 'Token copied to clipboard',
                        timer: 1500,
                        showConfirmButton: false,
                        position: 'top-end',
                        toast: true
                    });
                })
                .catch(err => {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to copy token',
                        timer: 3000,
                        showConfirmButton: false
                    });
                });
        }
    }

    const confirmBlockToken = (tokenId, username) => {
        Swal.fire({
            title: 'Confirm Block Token',
            html: `
                <p class="small">Are you sure you want to block token <strong>${username}</strong>?</p>
                <p class="small">Blocking this token will prevent it from being used to send SMS messages.</p>
                <p class="small">Please type <span class="text-danger fw-bold">"block token"</span> to confirm.</p>
                <input type="text" id="swal-confirm-input" class="swal2-input small" placeholder='Type "block token" here'>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: `<i class='bi bi-check'></i> Block Token`,
            cancelButtonText: `<i class='bi bi-x'></i> Cancel`,
            customClass: {
                html: 'small',
                title: 'card-title',
                confirmButton: 'btn btn-success me-2 btn-sm',
                cancelButton: 'btn btn-danger me-2 btn-sm'
            },
            preConfirm: () => {
                const inputValue = document.getElementById('swal-confirm-input').value;
                if (inputValue !== 'block token') {
                    Swal.showValidationMessage('Please type "block token" to confirm');
                    return false;
                }
                return true;
            }
        }).then((result) => {
            if (result.isConfirmed) {
                blockToken(tokenId);
            }
        });
    }

    const blockToken = (tokenId) => {
        smsgatewayService.blockToken(clinicId, tokenId)
            .then(res => {
                getTokenList();
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Token blocked successfully',
                    timer: 3000,
                    showConfirmButton: false
                });
            })
            .catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error blocking token',
                    timer: 3000,
                    showConfirmButton: false
                });
            });
    }

    const confirmUnblockToken = (tokenId, username) => {
        Swal.fire({
            title: 'Unblock Token',
            html: `Are you sure you want to unblock token <strong>${username}</strong>?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `<i class='bi bi-check'></i> Yes, unblock it!`,
            cancelButtonText: `<i class='bi bi-x'></i> Cancel`,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-success me-2 btn-sm',
                cancelButton: 'btn btn-danger me-2 btn-sm'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                unblockToken(tokenId);
            }
        });
    }

    const unblockToken = (tokenId) => {
        smsgatewayService.unblockToken(clinicId, tokenId)
            .then(res => {
                getTokenList();
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Token unblocked successfully',
                    timer: 3000,
                    showConfirmButton: false
                });
            })
            .catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error unblocking token',
                    timer: 3000,
                    showConfirmButton: false
                });
            });
    }

    const confirmCreateToken = () => {
        Swal.fire({
            title: 'Create New Token',
            text: 'Are you sure you want to create a new SMS gateway token?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `<i class='bi bi-check'></i> Yes, create it!`,
            cancelButtonText: `<i class='bi bi-x'></i> Cancel`,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-success me-2 btn-sm',
                cancelButton: 'btn btn-danger me-2 btn-sm'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                createToken();
            }
        });
    }

    const createToken = () => {
        setCreatingToken(true);
        smsgatewayService.createToken(clinicId)
            .then(res => {
                getTokenList();
                setCreatingToken(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Token created successfully',
                    timer: 3000,
                    showConfirmButton: false
                });
            })
            .catch(err => {
                setCreatingToken(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error creating token',
                    timer: 3000,
                    showConfirmButton: false
                });
            });
    }

    const readableDate = (date) => {
        const d = new Date(date);
        return d.toLocaleString();
    }

    const handleCloseTokenModal = () => {
        setShowTokenModal(false);
        setSelectedToken(null);
    }

    return (
        <div className="card card-padding shadow-none">
            <div className="d-flex justify-content-between align-items-center">
                <div className="card-title">
                    <i className="bi bi-chat-text me-2"></i>SMS Gateway Tokens
                </div>
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={confirmCreateToken}
                    disabled={loading || creatingToken}
                >
                    {creatingToken ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-key me-2"></i>Create New Token
                        </>
                    )}
                </button>
            </div>

            <div className="card shadow-none">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading tokens...</p>
                        </div>
                    ) : tokens.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-key fs-1 text-muted"></i>
                            <h5 className="mt-3 text-muted">No tokens found</h5>
                            <p className="text-muted">Get started by creating your first SMS gateway token</p>
                            <button
                                className="btn btn-primary mt-2"
                                onClick={confirmCreateToken}
                                disabled={creatingToken}
                            >
                                {creatingToken ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-plus-circle me-2"></i>Create Token
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle small">
                                <thead className="table-light">
                                <tr>
                                    <th scope="col">Token</th>
                                    <th scope="col">Role</th>
                                    <th scope="col">Created At</th>
                                    <th scope="col">Status</th>
                                    <th scope="col" className="text-end">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {tokens.map((token) => (
                                    <tr key={token.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-key text-primary me-2"></i>
                                                <span className="font-monospace">{token.username}</span>
                                            </div>
                                        </td>
                                        <td>{token.role || 'N/A'}</td>
                                        <td>{readableDate(token.createdAt)}</td>
                                        <td>
                                                <span className={`badge ${token.isActive ? 'bg-success' : 'bg-danger'}`}>
                                                    {token.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            {token.isBlocked && (
                                                <span className="badge bg-warning ms-1">Blocked</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <div className="btn-group" role="group">
                                                <button
                                                    className="btn btn-outline-info btn-sm"
                                                    onClick={() => revealToken(token)}
                                                    title="Reveal Token"
                                                >
                                                    <i className="bi bi-eye me-1"></i>Reveal Token
                                                </button>
                                                {token.isBlocked ? (
                                                    <button
                                                        className="btn btn-outline-success btn-sm"
                                                        onClick={() => confirmUnblockToken(token.id, token.username)}
                                                        title="Unblock Token"
                                                    >
                                                        <i className="bi bi-unlock me-1"></i>Unblock
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => confirmBlockToken(token.id, token.username)}
                                                        title="Block Token"
                                                    >
                                                        <i className="bi bi-lock me-1"></i>Block
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Token Reveal Modal */}
            <Modal show={showTokenModal} onHide={handleCloseTokenModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-key me-2"></i>
                        Token Details - {selectedToken?.username}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedToken && (
                        <div className="token-details">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Token ID:</strong>
                                    <div className="font-monospace small">{selectedToken.id}</div>
                                </div>
                                <div className="col-md-6">
                                    <strong>Username:</strong>
                                    <div className="font-monospace small">{selectedToken.username}</div>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Role:</strong>
                                    <div>{selectedToken.role || 'N/A'}</div>
                                </div>
                                <div className="col-md-6">
                                    <strong>Status:</strong>
                                    <div>
                                        <span className={`badge ${selectedToken.isActive ? 'bg-success' : 'bg-danger'}`}>
                                            {selectedToken.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        {selectedToken.isBlocked && (
                                            <span className="badge bg-warning ms-1">Blocked</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <strong>Created:</strong>
                                    <div>{readableDate(selectedToken.createdAt)}</div>
                                </div>
                                <div className="col-md-6">
                                    <strong>Modified:</strong>
                                    <div>{readableDate(selectedToken.modifiedAt)}</div>
                                </div>
                            </div>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>Token:</strong>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={copyTokenToClipboard}
                                        title="Copy Token to Clipboard"
                                    >
                                        <i className="bi bi-clipboard me-1"></i>Copy Token
                                    </Button>
                                </div>
                                <div className="token-display-container">
                                    <pre className="token-pre p-3 bg-light rounded small font-monospace">
                                        {selectedToken.token}
                                    </pre>
                                </div>
                                <div className="text-muted small mt-2">
                                    <i className="bi bi-info-circle me-1"></i>
                                    This token should be kept secure and not shared unnecessarily.
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" className="btn-sm" onClick={handleCloseTokenModal}>
                        <i className="bi bi-x me-1"></i>Close
                    </Button>
                    <Button variant="primary" className="btn-sm" onClick={copyTokenToClipboard}>
                        <i className="bi bi-clipboard me-1"></i>Copy Token
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default SmsgatewayTab;